import { EnumValue } from '../utils/utility.types';

export const VpnTopologyType = {
    FullMesh: 'FULL_MESH',
    Custom: 'CUSTOM',
    friendlyString(entry: string) {
        const map = {
            [this.FullMesh]: 'Full Mesh',
            [this.Custom]: 'Custom',
        };
        return map[entry as unknown as keyof typeof map] || entry;
    },
} as const;

export const VpnSetting = {
    // order of settings here matters and is used to sort settings when showing ChangedSettings modal

    // WPC Settings
    VpnTopology: 'VPN_TOPOLOGY',
    DefaultVpnRegion: 'DEFAULT_VPN_REGION',
    SNAT: 'SNAT',
    VpnSubnets: 'VPN_SUBNET_CONNECTOR_AND_USER',
    SplitSubnet: 'SPLIT_SUBNET',
    ClientOptions: 'CLIENT_OPTIONS',

    // Users settings
    VpnSessionTimeout: 'VPN_SESSION_TIMEOUT',
    ConnectAuth: 'DEFAULT_CONNECT_AUTH_TYPE',
    DeviceAllowance: 'DEVICE_ALLOWANCE_PER_USER',
    ForceUpdateDeviceAllowance: 'FORCE_UPDATE_DEVICE_ALLOWANCE',
    ProfileDistribution: 'PROFILE_DISTRIBUTION',
    DeviceEnforcementLevel: 'DEVICE_ENFORCEMENT_LEVEL',

    // User Auth settings
    TOPT: 'TOTP_ENABLED',
    AllowedTrustedDevices: 'TRUSTED_DEVICES_ALLOWED',

    // Dns settings
    UseOpenVpnDnsServer: 'USE_OPEN_VPN_DNS_SERVER',
    DnsServerAddresses: 'DNS_SERVER_ADDRESSES',
    DnsProxyEnabled: 'DNS_PROXY_ENABLED',
    DnsDefaultSuffix: 'DEFAULT_DNS_SUFFIX',
    DnsZones: 'DNS_ZONES',

    // Other settings
    AccountType: 'USAGE_SCENARIO',
    UserGroupSyncSaml: 'USER_GROUP_SYNC_SAML',
    UserGroupSyncLdap: 'USER_GROUP_SYNC_LDAP',

    // Settings not displayed on UI
    IpGenerationInProgress: 'IP_ADDRESS_REGENERATION_IN_PROGRESS',

    /**
     * @deprecated
     */
    EnableShield: 'ENABLE_SHIELD',
    /**
     * @deprecated
     */
    EnableShieldV2: 'ENABLE_SHIELD_UIV2',

    /**
     * @deprecated
     */
    EnableAV: 'ENABLE_ACCESS_VISIBILITY_UI',

    /**
     * @deprecated
     */
    EnablePublishing: 'ENABLE_PUBLISHING_UI',

    /**
     * @deprecated
     */
    EnableLogStreaming: 'ENABLE_LOG_STREAMING_UI',

    /**
     * @deprecated in 1.5.3
     */
    EnableLocationContext: 'ENABLE_LOCATION_CONTEXT',

    /**
     * @deprecated in 1.5.3
     */
    EnableDevicePosture: 'ENABLE_DEVICE_POSTURE',

    EnableAuditLog: 'ENABLE_AUDIT_LOG_UI',
    EnableDomainResolutions: 'ENABLE_DNS_RESOLUTION_UI',

    friendlyString(entry: string) {
        const map = {
            [this.ProfileDistribution]: 'Profile Distribution',
            [this.DeviceAllowance]: 'Device Allowance',
            [this.ForceUpdateDeviceAllowance]: 'Apply changes to existing User Groups',
            [this.VpnTopology]: 'Topology',
            [this.VpnSessionTimeout]: 'Connection Timeout',
            [this.SNAT]: 'SNAT',
            [this.ClientOptions]: 'OpenVPN Client Options',
            [this.UseOpenVpnDnsServer]: 'DNS Servers',
            [this.DefaultVpnRegion]: 'Default Region',
            [this.TOPT]: 'Two\u2060-\u2060Factor Authentication',
            [this.AllowedTrustedDevices]: 'Allow Trusted Devices',
            [this.ConnectAuth]: 'Connect Auth',
            [this.DeviceEnforcementLevel]: 'Device Identity Verification and Enforcement (DIVE)',

            [`${this.DnsServerAddresses}.primaryIpV4`]: 'Primary DNS (IPv4)',
            [`${this.DnsServerAddresses}.secondaryIpV4`]: 'Secondary DNS (IPv4)',
            [`${this.DnsServerAddresses}.primaryIpV6`]: 'Primary DNS (IPv6)',
            [`${this.DnsServerAddresses}.secondaryIpV6`]: 'Secondary DNS (IPv6)',
            [this.DnsProxyEnabled]: 'DNS Proxy',
            [this.DnsDefaultSuffix]: 'Default DNS Suffix',
            [this.DnsZones]: 'DNS Zones',

            [`${this.VpnSubnets}.ipV4Address`]: 'IPv4 WPC Subnets',
            [`${this.VpnSubnets}.ipV6Address`]: 'IPv6 WPC Subnets',

            [`${this.SplitSubnet}.ipV4Address`]: 'IPv4 Domain Routing Subnets',
            [`${this.SplitSubnet}.ipV6Address`]: 'IPv6 Domain Routing Subnets',
        };
        return map[entry] || entry;
    },
} as const;

export const ProfileDistributionType = {
    Automatic: 'AUTOMATIC',
    Manual: 'MANUAL',

    friendlyString(value: boolean | string) {
        if (typeof value === 'boolean') {
            return value ? 'Automatic' : 'Manual';
        }

        return value === this.Automatic ? 'Automatic' : 'Manual';
    },
} as const;

export const ConnectAuthType = {
    Auto: 'AUTO',
    Auth: 'AUTH',
    Strict: 'STRICT_AUTH',

    friendlyString(entry: string) {
        const map = {
            [this.Auto]: 'No',
            [this.Auth]: 'On prior auth timeout',
            [this.Strict]: 'Every time',
        };
        return map[entry as unknown as keyof typeof map] || entry;
    },
} as const;

export const UserGroupConnectTypeOptions = [
    ConnectAuthType.Auto,
    ConnectAuthType.Auth,
    ConnectAuthType.Strict,
].map(t => ({ name: ConnectAuthType.friendlyString(t), value: t }));

export const DeviceEnforcementLevel = {
    Off: 0,
    LearnAndEnforce: 1,
    Enforce: 2,

    friendlyString(entry: number) {
        return {
            [this.Off]: 'Off',
            [this.LearnAndEnforce]: 'Learn and Enforce',
            [this.Enforce]: 'Enforce',
        }[entry];
    },
} as const;

export type VpnSettingValue = EnumValue<typeof VpnSetting>;
export type ProfileDistributionTypeValue = EnumValue<typeof ProfileDistributionType>;
export type ConnectAuthTypeValue = EnumValue<typeof ConnectAuthType>;
export type DeviceEnforcementLevelValue = EnumValue<typeof DeviceEnforcementLevel>;
